import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaUpload, FaFile, FaTrash, FaDownload, FaEye, FaCheckCircle, FaTimesCircle, FaSpinner, FaClock, FaExclamationTriangle, FaFileExcel, FaFileCsv, FaFileAlt, FaFileImage, FaFilePdf, FaCloudUploadAlt, FaFolderOpen } from 'react-icons/fa';
import { apiClient } from '../utils/apiClient';
import ConfirmDialog from './ConfirmDialog';
export const FileUploadManager = ({ tenantId, workflowId, selectedFileIds, onFileUploaded, onFileDeleted, allowedTypes = ['xlsx', 'csv', 'json', 'txt', 'md'], maxFileSize = 50, maxFiles = 10, className = '' }) => {
    const [files, setFiles] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(new Map());
    const [isDragOver, setIsDragOver] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deletingFile, setDeletingFile] = useState(false);
    const fileInputRef = useRef(null);
    const visibleFiles = useMemo(() => {
        if (workflowId) {
            return files.filter((file) => file.workflow_id === workflowId);
        }
        if (selectedFileIds) {
            const selectedIdSet = new Set(selectedFileIds);
            return files.filter((file) => selectedIdSet.has(file.file_id));
        }
        return files;
    }, [files, selectedFileIds, workflowId]);
    const inFlightUploadCount = useMemo(() => Array.from(uploadProgress.values()).filter((progress) => progress.status !== 'failed').length, [uploadProgress]);
    const availableUploadSlots = Math.max(maxFiles - visibleFiles.length - inFlightUploadCount, 0);
    const normalizeWorkflowFile = useCallback((file) => ({
        ...file,
        filename: file.original_name || file.filename || 'unknown',
        file_size: file.size_bytes ?? file.file_size ?? 0,
        user_id: file.uploaded_by_user_id || file.user_id || 'unknown',
        upload_date: file.created_at || file.upload_date,
        status: file.is_active ? 'completed' : 'failed',
    }), []);
    // ファイル一覧取得
    const fetchFiles = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            // API構造に合わせて修正
            const response = await apiClient.susugi.files.getFiles(tenantId);
            if (response && response.data && Array.isArray(response.data.files)) {
                const extendedFiles = response.data.files.map(file => normalizeWorkflowFile(file));
                setFiles(extendedFiles);
            }
        }
        catch (error) {
            console.error('Failed to fetch files:', error);
            setError('ファイル一覧の取得に失敗しました');
            setFiles([]);
        }
        finally {
            setIsLoading(false);
        }
    }, [tenantId]);
    // 初回読み込み
    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);
    // ファイル型のアイコンを取得
    const getFileIcon = (fileName, mimeType) => {
        const extension = fileName ? fileName.split('.').pop()?.toLowerCase() : undefined;
        const type = mimeType?.toLowerCase();
        if (extension === 'xlsx' || extension === 'xls' || type?.includes('spreadsheet')) {
            return _jsx(FaFileExcel, { className: "text-green-500" });
        }
        else if (extension === 'csv' || type?.includes('csv')) {
            return _jsx(FaFileCsv, { className: "text-blue-500" });
        }
        else if (extension === 'pdf' || type?.includes('pdf')) {
            return _jsx(FaFilePdf, { className: "text-red-500" });
        }
        else if (extension === 'md' || extension === 'txt' || type?.includes('markdown') || type?.includes('text')) {
            return _jsx(FaFileAlt, { className: "text-gray-500" });
        }
        else if (type?.includes('image')) {
            return _jsx(FaFileImage, { className: "text-purple-500" });
        }
        else {
            return _jsx(FaFileAlt, { className: "text-gray-500" });
        }
    };
    // ファイルサイズをフォーマット
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    // ファイルバリデーション
    const validateFile = (file) => {
        const extension = file.name ? file.name.split('.').pop()?.toLowerCase() : undefined;
        if (!extension || !allowedTypes.includes(extension)) {
            return `許可されていないファイル形式です。対応形式: ${allowedTypes.join(', ')}`;
        }
        if (file.size > maxFileSize * 1024 * 1024) {
            return `ファイルサイズが制限を超えています。最大: ${maxFileSize}MB`;
        }
        if (visibleFiles.length >= maxFiles) {
            return `ファイル数が上限に達しています。最大: ${maxFiles}個`;
        }
        return null;
    };
    // ファイルアップロード処理
    const uploadFile = async (file) => {
        const validationError = validateFile(file);
        if (validationError) {
            setError(validationError);
            return;
        }
        const fileId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // アップロード進捗初期化
        setUploadProgress(prev => new Map(prev).set(fileId, {
            file_id: fileId,
            progress: 0,
            status: 'uploading'
        }));
        try {
            setError(null);
            // 既存のAPIに合わせてファイルアップロード
            const metadata = workflowId ? { workflow_id: workflowId } : {};
            const response = await apiClient.susugi.files.uploadFile(tenantId, file, metadata);
            if (response && response.data && response.data.file_id) {
                // アップロード完了
                setUploadProgress(prev => new Map(prev).set(fileId, {
                    file_id: fileId,
                    progress: 100,
                    status: 'completed'
                }));
                // ファイル一覧を再取得
                await fetchFiles();
                // コールバック呼び出し（簡略化されたオブジェクト）
                if (onFileUploaded) {
                    onFileUploaded(normalizeWorkflowFile(response.data));
                }
                // 進捗状態をクリア（2秒後）
                setTimeout(() => {
                    setUploadProgress(prev => {
                        const newMap = new Map(prev);
                        newMap.delete(fileId);
                        return newMap;
                    });
                }, 2000);
            }
        }
        catch (error) {
            console.error('File upload failed:', error);
            setUploadProgress(prev => new Map(prev).set(fileId, {
                file_id: fileId,
                progress: 0,
                status: 'failed',
                error_message: 'アップロードに失敗しました'
            }));
            setError('ファイルのアップロードに失敗しました');
        }
    };
    // ファイル削除
    const handleConfirmDeleteFile = async () => {
        if (!fileToDelete)
            return;
        setDeletingFile(true);
        try {
            await apiClient.susugi.files.deleteFile(tenantId, fileToDelete);
            // 一覧から削除
            setFiles(prev => prev.filter(f => f.file_id !== fileToDelete));
            if (onFileDeleted) {
                onFileDeleted(fileToDelete);
            }
            setFileToDelete(null);
        }
        catch (error) {
            console.error('File deletion failed:', error);
            setError('ファイルの削除に失敗しました');
        }
        finally {
            setDeletingFile(false);
        }
    };
    // ドラッグ&ドロップハンドラー
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        const droppedFiles = Array.from(e.dataTransfer.files).slice(0, availableUploadSlots);
        droppedFiles.forEach(uploadFile);
    };
    // ファイル選択ハンドラー
    const handleFileSelect = (e) => {
        const selectedFiles = Array.from(e.target.files || []).slice(0, availableUploadSlots);
        selectedFiles.forEach(uploadFile);
        // input値をリセット
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    // ファイル選択ダイアログを開く
    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    return (_jsxs("div", { className: `w-full ${className}`, children: [error && (_jsx("div", { className: "mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded", children: _jsxs("div", { className: "flex", children: [_jsx(FaExclamationTriangle, { className: "text-red-400 mt-0.5 mr-2" }), _jsx("span", { className: "text-red-700", children: error })] }) })), _jsxs("div", { className: `border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragOver
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-gray-400'}`, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [_jsx(FaCloudUploadAlt, { className: "mx-auto text-4xl text-gray-400 mb-4" }), _jsx("p", { className: "text-lg font-medium text-gray-900 mb-2", children: "\u30D5\u30A1\u30A4\u30EB\u3092\u30C9\u30E9\u30C3\u30B0&\u30C9\u30ED\u30C3\u30D7 \u307E\u305F\u306F \u30AF\u30EA\u30C3\u30AF\u3057\u3066\u9078\u629E" }), _jsxs("p", { className: "text-sm text-gray-500 mb-4", children: ["\u5BFE\u5FDC\u5F62\u5F0F: ", allowedTypes.join(', '), " | \u6700\u5927\u30B5\u30A4\u30BA: ", maxFileSize, "MB | \u6700\u5927\u6570: ", maxFiles, "\u500B"] }), _jsxs("button", { type: "button", onClick: openFileDialog, className: "inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500", children: [_jsx(FaUpload, { className: "mr-2" }), "\u30D5\u30A1\u30A4\u30EB\u3092\u9078\u629E"] }), _jsx("input", { ref: fileInputRef, type: "file", multiple: maxFiles > 1, accept: allowedTypes.map(type => `.${type}`).join(','), onChange: handleFileSelect, className: "hidden" })] }), uploadProgress.size > 0 && (_jsxs("div", { className: "mt-6", children: [_jsxs("h4", { className: "text-lg font-medium text-gray-900 mb-3", children: [_jsx(FaSpinner, { className: "inline animate-spin mr-2" }), "\u30A2\u30C3\u30D7\u30ED\u30FC\u30C9\u4E2D..."] }), Array.from(uploadProgress.values()).map((progress) => (_jsxs("div", { className: "bg-white p-4 rounded-lg border mb-2", children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsx("span", { className: "text-sm font-medium text-gray-900", children: progress.file_id }), _jsxs("span", { className: "text-sm text-gray-500", children: [progress.progress, "%"] })] }), _jsx("div", { className: "w-full bg-gray-200 rounded-full h-2", children: _jsx("div", { className: `h-2 rounded-full transition-all ${progress.status === 'failed'
                                        ? 'bg-red-500'
                                        : progress.status === 'completed'
                                            ? 'bg-green-500'
                                            : 'bg-indigo-600'}`, style: { width: `${progress.progress}%` } }) }), progress.error_message && (_jsx("p", { className: "text-sm text-red-600 mt-2", children: progress.error_message }))] }, progress.file_id)))] })), _jsxs("div", { className: "mt-6", children: [_jsxs("div", { className: "flex items-center justify-between mb-4", children: [_jsxs("h4", { className: "text-lg font-medium text-gray-900 flex items-center", children: [_jsx(FaFolderOpen, { className: "mr-2" }), "\u30A2\u30C3\u30D7\u30ED\u30FC\u30C9\u6E08\u307F\u30D5\u30A1\u30A4\u30EB (", visibleFiles.length, ")"] }), isLoading && _jsx(FaSpinner, { className: "animate-spin text-gray-400" })] }), visibleFiles.length === 0 && !isLoading ? (_jsxs("div", { className: "text-center py-8 text-gray-500", children: [_jsx(FaFile, { className: "mx-auto text-4xl mb-2" }), _jsx("p", { children: "\u30A2\u30C3\u30D7\u30ED\u30FC\u30C9\u3055\u308C\u305F\u30D5\u30A1\u30A4\u30EB\u306F\u3042\u308A\u307E\u305B\u3093" })] })) : (_jsx("div", { className: "grid gap-4 md:grid-cols-2 lg:grid-cols-3", children: visibleFiles.map((file) => (_jsxs("div", { className: "bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow", children: [_jsxs("div", { className: "flex items-start justify-between mb-3", children: [_jsxs("div", { className: "flex items-center flex-1 min-w-0", children: [_jsx("div", { className: "flex-shrink-0 mr-3", children: getFileIcon(file.original_name || file.filename || '', file.mime_type) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("p", { className: "text-sm font-medium text-gray-900 truncate", title: file.original_name || file.filename, children: file.original_name || file.filename }), _jsx("p", { className: "text-xs text-gray-500", children: formatFileSize(file.size_bytes ?? file.file_size ?? 0) })] })] }), _jsx("div", { className: "flex items-center ml-2", children: file.status === 'completed' ? (_jsx(FaCheckCircle, { className: "text-green-500" })) : file.status === 'failed' ? (_jsx(FaTimesCircle, { className: "text-red-500" })) : file.status === 'processing' ? (_jsx(FaSpinner, { className: "animate-spin text-blue-500" })) : (_jsx(FaClock, { className: "text-yellow-500" })) })] }), _jsxs("div", { className: "text-xs text-gray-500 mb-3", children: [_jsxs("p", { children: ["\u30A2\u30C3\u30D7\u30ED\u30FC\u30C9\u65E5\u6642: ", new Date(file.created_at || file.upload_date || '').toLocaleString('ja-JP')] }), _jsxs("p", { children: ["\u30A2\u30C3\u30D7\u30ED\u30FC\u30C9\u8005: ", file.uploaded_by_user_id || file.user_id] }), _jsxs("p", { children: ["\u30D5\u30A1\u30A4\u30EB\u5F62\u5F0F: ", file.file_type.toUpperCase()] }), file.metadata?.rows_count && (_jsxs("p", { children: ["\u30EC\u30B3\u30FC\u30C9\u6570: ", file.metadata.rows_count.toLocaleString(), "\u884C"] }))] }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex space-x-2", children: [_jsx("button", { type: "button", className: "text-indigo-600 hover:text-indigo-900 text-sm", title: "\u30D7\u30EC\u30D3\u30E5\u30FC", children: _jsx(FaEye, {}) }), _jsx("button", { type: "button", className: "text-green-600 hover:text-green-900 text-sm", title: "\u30C0\u30A6\u30F3\u30ED\u30FC\u30C9", children: _jsx(FaDownload, {}) })] }), _jsx("button", { type: "button", onClick: () => setFileToDelete(file.file_id), className: "text-red-600 hover:text-red-900 text-sm", title: "\u524A\u9664", children: _jsx(FaTrash, {}) })] })] }, file.file_id))) }))] }), _jsx(ConfirmDialog, { isOpen: !!fileToDelete, onClose: () => setFileToDelete(null), onConfirm: handleConfirmDeleteFile, danger: true, busy: deletingFile, title: "\u30D5\u30A1\u30A4\u30EB\u524A\u9664\u306E\u78BA\u8A8D", message: "\u3053\u306E\u30D5\u30A1\u30A4\u30EB\u3092\u524A\u9664\u3057\u307E\u3059\u304B\uFF1F", confirmLabel: "\u524A\u9664" })] }));
};
export default FileUploadManager;
